import { Inject, Injectable, Optional } from '@angular/core';
import {
  FIELD_PLACEHOLDER,
  readBlobApiError,
  removeEmpty,
  resolveFieldLabel,
} from '@digilize/shared/utils/helpers/src';
import { APP_CONFIG } from '@digilize/shared/utils/tokens/src';
import { TRANSLOCO_SCOPE, TranslocoScope, TranslocoService } from '@jsverse/transloco';
import { Alert, AlertType, ApiError, AppConfig } from '@shared/definitions';
import { AlertConfig } from './alerts.interface';
import { GlobalAlertState } from './alert-bar/globalAlertState.service';

@Injectable({ providedIn: 'any' })
export class AlertService {
  constructor(
    // Modules provide this as a single scope, but some provide an array of them
    // (product does, and `multi: true` also yields an array), so never read `.scope` directly.
    @Optional() @Inject(TRANSLOCO_SCOPE) private translocoScope: TranslocoScope | TranslocoScope[],
    private translocoService: TranslocoService,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private globalAlertState: GlobalAlertState
  ) {}

  setAlert(alert: Alert): void {
    this.globalAlertState.setAlert(alert);
  }

  setSimpleAlert(type: AlertType, title: string, message: string, translated = false): void {
    const alert: Alert = {
      type,
      title: {
        message: title,
        translate: translated,
      },
      message: {
        message: message,
        translate: translated,
      },
    };

    this.setAlert(alert);
  }

  handleApiError(apiError: ApiError, { withExtra, time }: AlertConfig = {}) {
    const alert: Alert = {
      type: AlertType.Error,
      title: {
        message: 'Error',
        translate: false,
      },
      message: {
        message:
          this.appConfig.withBackendErrorFieldsMapping && this.translocoScope
            ? this.getAlertMessageWithErrorMapping(apiError, apiError?.info?.message || apiError?.message, withExtra)
            : this.getAlertMessage(apiError, apiError?.message, withExtra),
        translate: false,
      },
      requestId: apiError?.request_id,
      apiError: apiError ?? undefined,
      time: time || 15000,
    };
    this.setAlert(alert);
  }

  /**
   * For a failed `responseType: 'blob'` request (export downloads): resolves the error body via
   * `readBlobApiError` - falling back to the already-parsed `error?.error?.error` shape for a
   * non-blob error - and shows it. When the body isn't our JSON error envelope at all (a 404 from
   * a backend without the endpoint, an HTML error page, a network error), `readBlobApiError`
   * correctly resolves to `null`; `handleApiError` must never be called with that, so a generic
   * translated fallback is shown instead.
   */
  async handleBlobApiError(error: unknown, fallbackMessageKey: string, config?: AlertConfig): Promise<void> {
    const apiError = (await readBlobApiError(error)) ?? (error as { error?: { error?: ApiError } })?.error?.error;
    if (apiError) {
      this.handleApiError(apiError, config);
    } else {
      this.setSimpleAlert(AlertType.Error, 'Error', fallbackMessageKey, true);
    }
  }

  private getAlertMessage(apiError: ApiError, message: string, withExtra = false): string {
    if (apiError?.type === 'validation_error' && apiError?.info?.field) {
      if (apiError?.info?.field) {
        message += `<br> [field: ${apiError.info.field}]`;
      }
    }
    // if (apiError.request_id) {
    //   message += `<br> [id: ${apiError.request_id}]`;
    // }
    if (withExtra) {
      const { field, ...extraFields } = apiError.info ?? {};
      const fieldsToShow = removeEmpty(extraFields);
      for (const key in fieldsToShow) {
        const element = fieldsToShow[key];
        message += `<br> [${key}: ${element}]`;
      }
    }
    return this.insertMsgParams(message);
  }

  private insertMsgParams(message: string): string {
    return message?.replace('{{email}}', this.appConfig?.email);
  }

  private getAlertMessageWithErrorMapping(apiError: ApiError, message: string, withExtra = false): string {
    if (apiError?.info?.field && message?.includes(FIELD_PLACEHOLDER)) {
      message = message.replace(
        FIELD_PLACEHOLDER,
        resolveFieldLabel(apiError.info.field, (key) => this.translocoService.translate(key), this.scopeNames)
      );
    }

    if (withExtra) {
      // `message` is already the alert text and `kind` is an internal error code
      // (`required_{field}`) — printing either just repeats the raw template back at the user.
      const { field, message: infoMessage, kind, ...extraFields } = apiError?.info ?? {};
      const fieldsToShow = removeEmpty(extraFields);
      for (const key in fieldsToShow) {
        const element = fieldsToShow[key];
        message += `<br> [${key}: ${element}]`;
      }
    }
    return message;
  }

  private get scopeNames(): string[] {
    const scopes = Array.isArray(this.translocoScope) ? this.translocoScope : [this.translocoScope];
    return scopes
      .map((scope) => (typeof scope === 'string' ? scope : (scope?.alias ?? scope?.scope)))
      .filter(Boolean) as string[];
  }
}
